import { Link } from "@tanstack/react-router";
import { ArrowRight, Check } from "lucide-react";
import type { ReactNode } from "react";
import { PublicFooter, PublicHeader } from "@/components/landing/MissionReadyPublicSite";
import { useMarketingPageTracking } from "@/lib/use-engagement";

/**
 * Every public marketing page now renders inside the same `mr-public` design
 * system as the homepage showroom (Bebas Neue titles, Barlow body, black/red
 * tactical surfaces). Navigation and footer are the single public
 * header/footer components so product links can never drift between pages.
 * Authenticated app UI is untouched — it never imports this shell.
 */
export function MarketingShell({ children }: { children: ReactNode }) {
  // Page-view + engagement signals for every marketing route.
  useMarketingPageTracking();
  return (
    <div className="mr-public mr-legacy-marketing">
      <PublicHeader />
      <main>{children}</main>
      <PublicFooter />
    </div>
  );
}

/**
 * Shared site footer. Exported so standalone pages (legal, FAQ) that render
 * their own header still give visitors somewhere to go instead of dead-ending.
 */
export function MarketingFooter() {
  return (
    <div className="mr-public mr-standalone-footer">
      <PublicFooter />
    </div>
  );
}

export function MarketingHero({
  eyebrow, title, subtitle,
}: { eyebrow: string; title: ReactNode; subtitle: string }) {
  return (
    <section className="mr-product-hero">
      <div className="mr-grid-bg" aria-hidden="true" />
      <div className="mr-shell">
        <span className="mr-live-chip"><i /> {eyebrow}</span>
        <h1>{title}</h1>
        <p>{subtitle}</p>
        <div className="mr-hero-actions">
          <Link to="/signup" className="mr-button">START FREE <ArrowRight /></Link>
          <Link to="/login" className="mr-button-secondary">SIGN IN</Link>
        </div>
      </div>
    </section>
  );
}

export function FeatureGrid({
  items,
  heading = "What you get",
  subheading,
}: {
  items: { title: string; desc: string; icon?: string }[];
  heading?: string;
  subheading?: string;
}) {
  return (
    <section className="mr-capabilities">
      <div className="mr-shell">
        <div className="mr-section-heading">
          <h2>{heading}</h2>
        </div>
        {subheading && <p className="mr-section-intro">{subheading}</p>}
        <div className="mr-capability-grid">
          {items.map((it) => (
            <article key={it.title}>
              {it.icon && <span aria-hidden="true">{it.icon}</span>}
              <h3>{it.title}</h3>
              <p>{it.desc}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CtaBand({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <section className="mr-capabilities">
      <div className="mr-shell">
        <div className="mr-product-cta">
          <h2>{title}</h2>
          <p>{subtitle}</p>
          <Link to="/signup" className="mr-button">START FREE <ArrowRight /></Link>
          <span><Check /> 30-day trial · No credit card</span>
        </div>
      </div>
    </section>
  );
}
